import React from 'react';
import { Box, Typography, Button, Stack, alpha, useTheme, keyframes } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionContext';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { resolveDefaultAccessiblePath } from '../utils/getDefaultAccessiblePath';

const shimmer = keyframes`
  0% { opacity: 0.35; }
  50% { opacity: 0.85; }
  100% { opacity: 0.35; }
`;

export const NotFoundPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { isSuperAdmin } = useAccessControl();
  const isDark = theme.palette.mode === 'dark';

  const handleGoHome = () => {
    navigate(resolveDefaultAccessiblePath(hasPermission, isSuperAdmin), { replace: true });
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'min(70vh, 560px)',
        px: 2,
        py: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: -80,
          background: isDark
            ? `radial-gradient(ellipse 70% 50% at 50% 20%, ${alpha(theme.palette.primary.main, 0.18)}, transparent 55%),
               radial-gradient(ellipse 50% 40% at 80% 70%, ${alpha(theme.palette.secondary.main, 0.12)}, transparent 50%)`
            : `radial-gradient(ellipse 65% 45% at 50% 15%, ${alpha(theme.palette.primary.main, 0.14)}, transparent 50%),
               radial-gradient(ellipse 45% 35% at 85% 75%, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 45%)`,
          pointerEvents: 'none',
        }}
      />

      <Stack
        alignItems="center"
        spacing={3}
        sx={{ position: 'relative', maxWidth: 480, textAlign: 'center' }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.04em',
            lineHeight: 1.05,
            fontSize: { xs: '3rem', sm: '4.25rem' },
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            animation: `${shimmer} 3.2s ease-in-out infinite`,
          }}
        >
          404
        </Typography>

        <Stack spacing={1}>
          <Typography variant="h5" component="p" fontWeight={700}>
            Esta rota saiu pra café e não voltou
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
            O endereço pode estar errado, a página mudou de setor ou nunca existiu. Enquanto isso, o chão de fábrica
            continua rodando — só não é por aqui.
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1, width: '100%', maxWidth: 360 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGoHome}
            startIcon={<HomeOutlinedIcon />}
          >
            Ir para o início
          </Button>
          <Button variant="outlined" size="large" fullWidth onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
            Voltar
          </Button>
        </Stack>

        <Typography variant="caption" color="text.disabled">
          Código de erro: peça fantasma · linha de montagem desconhecida
        </Typography>
      </Stack>
    </Box>
  );
};
